import { Switch, SwitchProps, alpha, styled } from '@mui/material'
import React, { FC } from 'react'

interface ToggleSwitchProps extends SwitchProps {
  label?: string
}

const ToggleSwitch: FC<ToggleSwitchProps> = ({ label, onKeyDown, onChange, inputProps, ...props }) => {
  // a11y: Enter toggles the switch too (a native checkbox toggles on Space only). Click the
  // underlying input so React emits a real change event and Space/Enter stay symmetric.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.currentTarget.querySelector<HTMLInputElement>('input')?.click()
    }
    onKeyDown?.(e)
  }

  return (
    <StyledSwitch
      disableRipple
      {...props}
      onChange={onChange}
      onKeyDown={handleKeyDown}
      // a11y: render as role="switch" (native is a checkbox); callers can override via inputProps
      inputProps={{ role: 'switch', ...inputProps }}
    />
  )
}

const StyledSwitch = styled(Switch)(({ theme }) => ({
  width: '34px',
  height: '18px',
  padding: 0,
  overflow: 'initial',
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: theme.palette.common.white,
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.blue[500],
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        backgroundColor: theme.palette.blue[200],
      },
      '&.Mui-disabled .MuiSwitch-thumb': {
        backgroundColor: theme.palette.common.white,
      },
    },
    '&.Mui-focusVisible + .MuiSwitch-track': {
      borderColor: `${theme.palette.blue[300]}`,
      outline: `3px solid ${alpha(theme.palette.blue[200], 0.2)}`,
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      backgroundColor: theme.palette.common.white,
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 1,
      backgroundColor: 'var(--bluegrey-200)',
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: '14px',
    height: '14px',
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    border: `1px solid transparent`,
    backgroundColor: theme.palette.additionalColors['bg_500'],
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}))

export default ToggleSwitch
